import React, { useState } from 'react';
import { Link, Redirect } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/api';
import { LoginBackground } from '../components/auth/LoginBackground';
import { getHostTenantId } from '../config/api-config';

export function ForgotPasswordPage() {
  const { isAuthenticated, loading } = useAuth();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!loading && isAuthenticated) {
    return <Redirect to="/" />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setSubmitting(true);
    try {
      const hostTenantRaw = getHostTenantId();
      const tenantId =
        hostTenantRaw && /^\d+$/.test(hostTenantRaw) ? Number(hostTenantRaw) : undefined;
      if (!tenantId) {
        setError('Open this page from your school login URL to reset your school password.');
        return;
      }
      const res = await apiService.requestPasswordReset({
        email: email.trim(),
        tenant_id: tenantId,
      });
      setMessage(
        res.data?.message ||
          'If an account exists at this school, a reset link has been sent.'
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send reset email');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <LoginBackground />
      <div className="login-page__content flex min-h-screen items-center justify-center p-6">
        <div className="w-full max-w-md royal-card login-page__card p-8 shadow-royal-lg border border-primary-500/20">
          <h1 className="font-display text-xl font-bold text-secondary-900">Reset password</h1>
          <p className="text-sm text-secondary-600 mt-2">
            Enter your account email. We&apos;ll send a link to reset your password.
          </p>

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div>
              <label htmlFor="forgot-email" className="block text-sm font-medium text-secondary-700 mb-1">
                Email
              </label>
              <input
                id="forgot-email"
                type="email"
                required
                autoComplete="email"
                className="royal-input w-full px-3 py-2.5 text-sm"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            {error && (
              <p className="text-sm text-red-600" role="alert">
                {error}
              </p>
            )}
            {message && (
              <p className="text-sm text-green-700" role="status">
                {message}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full royal-btn-primary py-2.5 rounded-lg text-sm font-medium disabled:opacity-50"
            >
              {submitting ? 'Sending…' : 'Send reset link'}
            </button>
          </form>

          <p className="mt-6 text-sm text-secondary-600">
            <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
              Back to sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
